import axios, { InternalAxiosRequestConfig } from "axios";

import {
  getCognitoUsername,
  getAuthToken,
  getRefreshToken,
  setAuthToken,
  setRefreshToken,
  removeAuthToken,
  removeRefreshToken,
  persistCognitoUsernameForRecovery,
  savePostLoginRedirect,
} from "@/utils/authUtils";
import { getLocalizedErrorMessage } from "@/utils/errorMessageMapper";
import { saveLogoutReason, shouldForceLogoutOn401 } from "@/constants/clientAccess";
import { setupAxiosRetry } from "@/utils/axiosRetry";
/* eslint-disable @typescript-eslint/no-explicit-any */

const ipponApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  // Lambda コールドスタートで axios が無限 hang して loading が固まる問題対策。
  // timeout で ECONNABORTED 化 → axiosRetry が指数バックオフで自動リトライ → 温まれば成功。
  timeout: 30_000,
});

// リトライ設定を適用（最初に設定）
setupAxiosRetry(ipponApi, {
  maxAttempts: 3,
  baseDelay: 1000,
  maxDelay: 10000,
});

// リフレッシュ中フラグ（重複リクエストを防ぐ）
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

// キューの処理
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

// 最終的なログアウト処理。reason は BE の 401 の code（契約前・契約終了ならログイン画面で理由を出す）
function forceLogout(reason?: unknown) {
  // トークンをクリア
  removeAuthToken();
  removeRefreshToken();

  // ローカルストレージとセッションストレージもクリア
  localStorage.clear();
  sessionStorage.clear();

  // 契約による強制ログアウトは、理由をログイン画面で 1 回だけ出す（管理画面化 SPEC §7.4・clear の後に書く）
  saveLogoutReason(reason);

  // Reduxストアもクリアするためにイベントを発火
  window.dispatchEvent(new CustomEvent("auth-logout"));

  // 再ログイン後に元の画面へ戻すため、現在の URL を保存 (clear の後に書く)。
  const currentPath = window.location.pathname + window.location.search;
  savePostLoginRedirect(currentPath);

  // ログイン画面にリダイレクト
  window.location.href = "/login";
}

// JWTトークンの有効期限をチェック（バッファー時間: 30秒）
const isTokenExpiringSoon = (token: string): boolean => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return true;

    const payload = parts[1];
    // パディングを追加
    const paddedPayload = payload + "=".repeat((4 - (payload.length % 4)) % 4);
    // URLセーフ文字を標準Base64文字に変換
    const base64 = paddedPayload.replace(/-/g, "+").replace(/_/g, "/");

    const decodedPayload = JSON.parse(atob(base64));
    const exp = decodedPayload.exp;

    if (!exp) return true;

    const currentTime = Math.floor(Date.now() / 1000);
    const bufferTime = 30; // 30秒のバッファー

    return exp <= currentTime + bufferTime;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true; // エラーの場合は期限切れとして扱う
  }
};

// リフレッシュトークンを使ってトークンを更新
const refreshAuthToken = async (): Promise<string> => {
  const refreshToken = getRefreshToken();
  const username = getCognitoUsername();

  if (!refreshToken) {
    console.error("Refresh token not found in cookies");
    throw new Error("Refresh token not found");
  }

  if (!username) {
    console.error("Username not found in JWT token");
    throw new Error("Username not found");
  }

  try {
    // 循環参照を避けるため、新しいaxiosインスタンスを作成
    const refreshApi = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
    });

    console.log("Attempting token refresh...");
    const response = await refreshApi.post("/api/v1/auth/refresh-token", {
      refreshToken: refreshToken,
      username: username,
    });

    const { token } = response.data.data;

    if (!token || !token.IdToken) {
      console.error("Invalid token response:", response.data);
      throw new Error("Invalid token response from server");
    }

    // 新しいトークンをCookieに保存
    setAuthToken(token.IdToken, 1);
    // [RECOVERY] token 単独消失からの復旧用に username を更新しておく
    persistCognitoUsernameForRecovery();

    // 新しいRefreshTokenも保存（返却された場合）
    if (token.RefreshToken) {
      setRefreshToken(token.RefreshToken, 7);
      console.log("Token refresh successful with new refresh token");
    } else {
      console.log("Token refresh successful (refresh token not updated)");
    }

    return token.IdToken;
  } catch (error: any) {
    console.error("Token refresh failed:", {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
    });
    throw error;
  }
};

// [RECOVERY] token cookie が無く refreshToken だけ残っている状態からの
// 自動復旧を試みる。成功なら true (token cookie が新規発行されている)、
// 失敗なら false。同時実行は failedQueue で 1 回に集約される。
// PrivateRoute からも呼ぶので export。
export const tryRecoverAuth = async (): Promise<boolean> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  if (isRefreshing) {
    return new Promise<boolean>((resolve) => {
      failedQueue.push({
        resolve: (newToken: any) => resolve(!!newToken),
        reject: () => resolve(false),
      });
    });
  }

  isRefreshing = true;
  try {
    const newToken = await refreshAuthToken();
    processQueue(null, newToken);
    return true;
  } catch (e) {
    processQueue(e, null);
    return false;
  } finally {
    isRefreshing = false;
  }
};

// Authorization ヘッダ付与 + 期限切れ間近ならリフレッシュする request interceptor。
// ai-backend 用クライアント (aiBackendAxios) でも共有するため export。
export const attachAuthHeader = async (
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  // リフレッシュトークンエンドポイントの場合はトークンチェックをスキップ
  if (config.url?.includes("/auth/refresh-token")) {
    return config;
  }

  let token = getAuthToken();

  // [RECOVERY] token cookie が無いが refreshToken は残っている場合、
  // 復旧 (refresh して token を再発行) を試みる。成功すれば以降の
  // 通常パスで Authorization ヘッダが乗る。失敗ならそのまま BE へ流して
  // 401 → response interceptor の forceLogout に任せる。
  if (!token && getRefreshToken()) {
    const ok = await tryRecoverAuth();
    if (ok) {
      token = getAuthToken();
    }
  }

  if (token) {
    // トークンの有効期限をチェック
    if (isTokenExpiringSoon(token)) {
      // リフレッシュトークンが存在するかチェック
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        // refreshToken 無し。期限内 (30s バッファ内) の現行 token をそのまま送り、
        // BE 側で 401 になれば response interceptor が forceLogout する。
      } else if (isRefreshing) {
        // 既にリフレッシュ中の場合はキューに追加
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (newToken) => {
              if (newToken) {
                config.headers.Authorization = `Bearer ${newToken}`;
              }
              resolve(config);
            },
            reject,
          });
        });
      } else {
        // リフレッシュトークン処理を実行
        isRefreshing = true;

        try {
          const newToken = await refreshAuthToken();

          // キューを処理
          processQueue(null, newToken);

          // 新しいトークンを使用
          token = newToken;
        } catch (refreshError) {
          // [DEBUG] 旧: refresh 失敗 → 即 forceLogout。一時的に止めて、
          // 期限切れの古い token のまま BE まで通す。BE 側 log で「refresh が
          // どんな状況で失敗したか」と「次のリクエストで BE が 401 を出すか」
          // をペアで見る。
          processQueue(refreshError, null);
          // refresh 失敗。期限切れの古い token のまま BE まで通し、BE の 401 で
          // response interceptor が forceLogout する。機微情報はログしない。
        } finally {
          isRefreshing = false;
        }
      }
    }

    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
};

ipponApi.interceptors.request.use(attachAuthHeader, (error) => {
  return Promise.reject(error);
});

ipponApi.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // 401エラーの場合、プロアクティブ処理でカバーできなかったケースなので
    // 直接ログアウト処理を実行（セキュリティ重視）
    // ログイン API の 401（パスワード違い・契約終了など）はログイン画面にとどまって失敗の文言を出すため、
    // 強制ログアウト（ログイン画面への移動）をしない
    if (error.response?.status === 401 && shouldForceLogoutOn401(error.config?.url)) {
      // プロアクティブ処理でカバーできなかった 401 は直接ログアウト（セキュリティ重視）。
      // 契約前・契約終了（code 付き）は理由を持ってログイン画面へ戻す
      forceLogout(error.response?.data?.code);
      return Promise.reject(error);
    }

    // エラーレスポンスのメッセージを日本語化
    if (error.response?.data) {
      const localizedMessage = getLocalizedErrorMessage(error);
      error.response.data = {
        ...error.response.data,
        message: localizedMessage,
        status: error.response.status,
      };
    }

    return Promise.reject(error);
  }
);
/* eslint-enable @typescript-eslint/no-explicit-any */
export default ipponApi;
